import { z } from 'zod'
import { TenantIdSchema, UlidSchema, ShortIdSchema, IsoDateSchema, MoneySchema } from './common'
import { DiscogsCoverUrlSchema, DiscogsVideoSchema, ReleaseTrackSchema } from './discogs'

const BoxIdSchema = z.string().regex(/^[A-Z]{1,2}$/, 'box id "A".."ZZ"')

/**
 * Map a present-but-blank query value to `undefined` before numeric coercion.
 * Without this, `z.coerce.number()('')` is `0` (Number('') === 0), so a stray
 * `?priceMax=` or `?yearMax=` would coerce to 0 and silently empty the result.
 */
const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v)

export const RecordChannelSchema = z
  .enum(['store', 'online', 'other', 'not_for_sale', 'scrapped'])
  .openapi('RecordChannel')

/**
 * Discogs grading scale. Default per shop is "VG+" but the chosen grade is
 * stored per-record at intake so the shop owner can later filter or report on
 * it. Same letter codes as `GradePriceSchema` in `discogs.ts`.
 *
 * NOTE: the declaration order is the canonical quality ranking (M best → P worst)
 * and is LOAD-BEARING — `lib/sort.ts` (`CONDITION_RANK`) derives the condition sort
 * order from it. Reorder only if you mean to change that ranking.
 */
export const RecordConditionSchema = z
  .enum(['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'F', 'P'])
  .openapi('RecordCondition')

/**
 * Sleeve/cover grading scale. A SUPERSET of `RecordConditionSchema`: every
 * Discogs disc grade PLUS two cover-only descriptors Discogs recognises —
 * "Generic" (a plain unbranded/company sleeve) and "No cover" (record supplied
 * without any sleeve). These only make sense for the cover, never the disc, so
 * they live on this schema rather than `RecordConditionSchema`. Spreading the
 * disc grades keeps the two scales in lockstep — adding/removing a disc grade
 * flows through automatically. Backward-compatible with existing records: any
 * previously-stored sleeveCondition is, by construction, still a valid value.
 */
export const SleeveConditionSchema = z
  .enum([...RecordConditionSchema.options, 'Generic', 'No cover'])
  .openapi('SleeveCondition')

export const RecordStatusSchema = z
  .enum([
    'in_stock',
    'reserved',
    'sold',
    'sold_online',
    'fulfilled',
    'returned',
    // Physically gone but not sold — lost or stolen. A closed, non-sale state
    // that frees the record's slot (the physical copy is no longer present).
    'missing',
    'scrapped',
    'not_for_sale',
  ])
  .openapi('RecordStatus')

/**
 * Coarse "is this on the shelf for sale right now?" bucket, DERIVED from
 * `status` and stored on the item purely as a DynamoDB index key (GSI2). It
 * exists so the single most common catalogue query — "what do I currently have
 * in stock, across all channels" — is one bounded Query instead of reading
 * every record the shop has ever taken in. `active` stays bounded by shop
 * capacity; `closed` grows unbounded with history but is rarely listed.
 *
 *   active = in_stock | reserved   (physically present, sellable now)
 *   closed = everything else       (sold | sold_online | fulfilled |
 *                                   returned | missing | scrapped | not_for_sale)
 *
 * NOTE `returned` is treated as `closed`: a returned record is back in the shop
 * but pending re-intake/decision, not yet re-shelved stock. To make returns
 * count as live stock, move it into ACTIVE_STATUSES below and re-derive the
 * `gsi2pk`/`gsi2sk` of existing items.
 */
export const ShelfStateSchema = z.enum(['active', 'closed']).openapi('ShelfState')

export type ShelfState = z.infer<typeof ShelfStateSchema>

const ACTIVE_STATUSES: ReadonlySet<RecordStatus> = new Set<RecordStatus>(['in_stock', 'reserved'])

/** Derive the GSI2 shelf-state bucket from a record's lifecycle status. */
export function shelfStateFor(status: RecordStatus): ShelfState {
  return ACTIVE_STATUSES.has(status) ? 'active' : 'closed'
}

/**
 * The terminal SALE states: a copy sold in-store, sold online (awaiting shipment), or
 * shipped. In these states the sales channel no longer applies and the copy can't be
 * re-channelled or scrapped — only returned. Shared so the catalogue drawer (hides those
 * controls) and the records handlers (which reject the same moves) can't diverge.
 */
const SOLD_STATUSES: ReadonlySet<RecordStatus> = new Set<RecordStatus>([
  'sold',
  'sold_online',
  'fulfilled',
])
export function isSoldStatus(status: RecordStatus): boolean {
  return SOLD_STATUSES.has(status)
}

/**
 * Why a copy left the shelf. One flat enum so the field stored on the record has
 * a single type; each endpoint accepts only the subset that fits its outcome:
 *
 *   lost                  → status `missing`  (reversible)
 *   damaged / unsellable  → status `scrapped` (terminal, row kept for history)
 *   wrong_scan/ duplicate → hard delete       (row gone — the intake was a mistake)
 *   other                 → free text, legal on every path
 */
export const RemovalReasonSchema = z
  .enum(['lost', 'damaged', 'unsellable', 'wrong_scan', 'duplicate', 'other'])
  .openapi('RemovalReason')

/** Reasons legal when marking a copy missing (Vermist) — reversible. */
export const MissingReasonSchema = z.enum(['lost', 'other']).openapi('MissingReason')

/** Reasons legal on the scrap (Afdanken) path: the copy exists but can't be sold. */
export const ScrapReasonSchema = z.enum(['damaged', 'unsellable', 'other']).openapi('ScrapReason')

/** Reasons legal on the hard-delete path: the row itself was created in error. */
export const DeleteReasonSchema = z
  .enum(['wrong_scan', 'duplicate', 'other'])
  .openapi('DeleteReason')

/** Free-text detail. Required when the reason is `other`, optional otherwise. */
export const RemovalReasonNoteSchema = z.string().trim().min(1).max(300)

/**
 * Removal-reason consistency, mirroring `noteRequiresAReason` in
 * schemas/transactions.ts: a note with no reason is a malformed request (every
 * display path gates on the reason and would drop the note silently), and
 * `other` without a note is meaningless — the free text IS the reason.
 */
export const removalReasonRefinement = (
  v: { removalReason?: string | undefined; removalReasonNote?: string | undefined },
  ctx: z.RefinementCtx
): void => {
  if (v.removalReasonNote !== undefined && v.removalReason === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: 'removalReasonNote requires a removalReason to be set',
      path: ['removalReasonNote'],
    })
  }
  if (v.removalReason === 'other' && v.removalReasonNote === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: "removalReasonNote is required when removalReason is 'other'",
      path: ['removalReasonNote'],
    })
  }
}

/**
 * Lowercase + strip diacritics so "coltrane" matches "Coltrane"/"Coltráne". The
 * single source of truth for the search fold, shared by the backend text search
 * and the frontend's instant local filter so the two never disagree.
 */
export function normalizeForSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

/** Slot identifier: "A1" .. "ZZ9999" or null if record has no physical location. */
export const SlotIdSchema = z
  .string()
  .regex(/^[A-Z]{1,2}\d+$/)
  .nullable()
  .openapi('SlotId', { example: 'A47' })

/**
 * S3 object keys for the four customer-facing images of an online record.
 * Each is the opaque key returned by the presign endpoint (or null when that
 * image hasn't been uploaded). Unlike `photoKey` (the internal scan photo),
 * these are mutable post-intake — the user can re-crop/replace them later.
 *   front/back = sleeve (square crop) · sideA/sideB = record face (round crop)
 */
export const OnlineImagesSchema = z
  .object({
    front: z.string().nullable(),
    back: z.string().nullable(),
    sideA: z.string().nullable(),
    sideB: z.string().nullable(),
  })
  .openapi('OnlineImages')

/** All-null online-images value: applied to records with no online photos. */
const EMPTY_ONLINE_IMAGES = { front: null, back: null, sideA: null, sideB: null } as const

/**
 * Lifecycle of this record's listing on the Discogs Marketplace, or null when the
 * record was never offered there (store-only intake, or online intake with the
 * "Verkoop via Discogs Marketplace" box unchecked). Set by the server, never by
 * the client — intake only signals intent via `listOnDiscogs` on the create
 * request; the asynchronous listing worker fills in `listingId`/`status`.
 *
 *   pending  — queued for creation on Discogs (the worker hasn't run yet)
 *   listed   — live on Discogs; `listingId` builds the public sell URL
 *   failed   — creation failed (see `error`); the drawer offers a retry
 *   removed  — delisted again (record sold/reserved in-store, or scrapped)
 */
export const DiscogsListingStatusSchema = z
  .enum(['pending', 'listed', 'failed', 'removed'])
  .openapi('DiscogsListingStatus')

export const DiscogsListingSchema = z
  .object({
    status: DiscogsListingStatusSchema,
    /** Discogs listing id — the `<id>` in `discogs.com/sell/item/<id>`. Null until listed. */
    listingId: z.string().nullable(),
    /** Error code when `status === 'failed'` (e.g. ERR_DISCOGS_NO_RELEASE), else null. */
    error: z.string().nullable(),
    /**
     * Human-readable detail from Discogs for a failed op — e.g. the 422 body
     * "Invalid condition: expected string". Display-only (shown under the coded
     * message in the drawer); null unless `status === 'failed'`. `.default(null)`
     * so records written before this field existed still parse on read.
     */
    errorDetail: z.string().max(300).nullable().default(null),
    // Nullable dates via an explicit union, NOT `IsoDateSchema.nullable()`. This
    // component is registered (`.openapi('DiscogsListing')`) and emitted before
    // Record, so it's the FIRST nullable use of the shared `IsoDate` component —
    // and `.nullable()` on a registered ref there mutates the IsoDate component
    // itself to `type: [string, null]`, which then makes EVERY `$ref: IsoDate`
    // (createdAt across all entities) wrongly nullable and trips oasdiff's
    // breaking-change gate. The union references IsoDate without mutating it
    // (renders as `anyOf: [IsoDate, null]`). Keep it a union.
    listedAt: z.union([IsoDateSchema, z.null()]),
    removedAt: z.union([IsoDateSchema, z.null()]),
    /**
     * True when this listing was taken down by an explicit operator action
     * (`POST /records/{id}/delist`, the drawer's "Van Discogs verwijderen" button)
     * rather than a normal system flow (POS reserve/sale, a channel/scrap/transition
     * move, a Discogs-side sale). Only meaningful while `status === 'removed'`.
     * `buildReservationReleaseSet` (pos-reservations.ts) checks it before deciding
     * whether releasing a POS reservation should re-list the copy — a system-driven
     * removal is expected to be reversed that way, but an operator's manual pull-down
     * must stick until they explicitly relist. `.default(false)` so records written
     * before this field existed still parse on read.
     */
    manualRemoval: z.boolean().default(false),
  })
  .openapi('DiscogsListing')

/** A freshly-armed listing: queued for creation, not yet live on Discogs. */
export const pendingListing = (): z.infer<typeof DiscogsListingSchema> => ({
  status: 'pending',
  listingId: null,
  error: null,
  errorDetail: null,
  listedAt: null,
  removedAt: null,
  // A (re)list — the only path that puts a record back on Discogs — always clears
  // a prior manual removal; see the field doc above.
  manualRemoval: false,
})

/** A failed-to-list state carrying the error code (+ optional channel detail); the drawer offers a retry. */
export const failedListing = (
  error: string,
  errorDetail: string | null = null
): z.infer<typeof DiscogsListingSchema> => ({
  status: 'failed',
  listingId: null,
  error,
  errorDetail,
  listedAt: null,
  removedAt: null,
  manualRemoval: false,
})

/**
 * Wire-shape of a record. Persisted into DynamoDB alongside DDB-internal keys
 * (PK/SK/gsi*pk/gsi*sk/ttl) which zod silently strips on parse — so callers
 * receive a clean entity. DO NOT add `.strict()` here: that would make every
 * read fail because the raw DDB Item contains those internal attributes. If
 * you want stricter validation, parse against a separately-defined "input"
 * schema, not this one.
 */
export const RecordSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    shortId: ShortIdSchema,
    artist: z.string().min(1).max(200),
    title: z.string().min(1).max(200),
    /**
     * The mix/version line rendered under the title on the webshop detail page
     * (e.g. "The Escape House Mix"). No Discogs field is persisted for it at
     * intake — staff enter/correct it. Defaulted so records written before this
     * field existed still parse on read (the raw DDB item lacks the key).
     */
    subtitle: z.string().max(200).nullable().default(null),
    year: z.coerce.number().int().min(1900).max(2100).nullable(),
    country: z.string().max(80).nullable(),
    catno: z.string().max(80).nullable(),
    /**
     * Record label / imprint name (e.g. "Kling Klang"). Sourced from Discogs'
     * `labels[0].name` (or Claude Vision's reading of the sleeve) and kept
     * separate from `catno` (`labels[0].catno`) so the two aren't conflated in a
     * single combined string. Defaulted so records written before this field
     * existed still parse on read (the raw DDB item lacks the key).
     */
    label: z.string().max(120).nullable().default(null),
    /**
     * Physical format as the webshop facets show it ("LP", "2×LP", "12\"",
     * "7\""). Derivable from the Discogs release's formats at intake (the
     * intake UI maps it); staff can correct it via create/PATCH. Defaulted so
     * records written before this field existed still parse on read (the raw
     * DDB item lacks the key).
     */
    format: z.string().max(80).nullable().default(null),
    genre: z.string().max(80).nullable(),
    subgenre: z.string().max(80).nullable(),
    note: z.string().max(1000).nullable(),
    /**
     * Free-text description for the online shop only — any relevant information
     * about the record that does NOT belong in the printed `note` (the blurp /
     * "Persoonlijke noot"): provenance, defects not visible in photos, pressing
     * quirks, etc. Never rendered on the physical Dymo label. Defaulted to null
     * so records written before this field existed still parse on read (the raw
     * DDB item lacks the key), and so intake/PATCH may omit it.
     */
    description: z.string().max(2000).nullable().default(null),
    channel: RecordChannelSchema,
    status: RecordStatusSchema,
    price: MoneySchema,
    condition: RecordConditionSchema,
    /**
     * Condition of the sleeve/cover, graded on the same Discogs scale as
     * `condition` (which grades the disc itself). Discogs requires a separate
     * sleeve grade to list a record for sale, and surfacing it independently in
     * the UI makes the sleeve state explicit instead of conflating it with the
     * vinyl. Defaulted so records written before this field existed still parse
     * on read (the raw DDB item lacks the key). Uses the wider sleeve scale so
     * "Generic" / "No cover" can be recorded for the cover.
     */
    sleeveCondition: SleeveConditionSchema.default('VG+'),
    /**
     * Whether this copy was new (sealed/unplayed) at intake — determines VAT
     * treatment (21% standard vs. the second-hand margin scheme, see
     * `VatTreatmentSchema` in schemas/transactions.ts). Captured once at
     * intake and immutable thereafter: a new record may later be re-graded to
     * a lower condition/price as it deteriorates, but it remains a 21%-VAT
     * item. Mistakes are corrected by deleting and re-scanning, not by
     * editing this flag — see UpdateRecordRequestSchema, which omits it.
     * Defaulted false so records written before this field existed still
     * parse on read (the raw DDB item lacks the key).
     */
    isNew: z.boolean().default(false),
    supplierId: UlidSchema.nullable(),
    /**
     * Percentage of the sale price that goes to the supplier at sale time
     * (0..100). Snapshotted from the supplier's defaultSharePercent when the
     * record is intaked so later supplier-config changes don't retroactively
     * affect historical payouts.
     */
    supplierShareSnapshot: z.number().min(0).max(100).nullable(),
    locationId: SlotIdSchema,
    photoKey: z.string().nullable(),
    discogsReleaseId: z.string().nullable(),
    /**
     * The Discogs release's sleeve image: a signed `i.discogs.com` URL, never the
     * image. The staff catalogue shows it, badged "Discogs", only when the record
     * has no photo of its own. Deliberately NOT in the storefront projection
     * (`toShopRecord`): it shows the pressing, not this copy, and Discogs' API
     * terms restrict republishing. Set at intake or by
     * scripts/backfill-discogs-cover.ts; immutable after, like discogsReleaseId.
     */
    discogsCoverUrl: z.union([DiscogsCoverUrlSchema, z.null()]).default(null),
    /**
     * Listen-snapshot: the Discogs release's YouTube links and tracklist rows,
     * copied once at intake from the release the operator matched, so the
     * record drawer can offer playback without re-hitting the shared 60/min
     * Discogs budget. `null` = no snapshot (pre-feature record, or intake
     * without a Discogs match) — the frontend then lazy-fetches via
     * /discogs/release/{id} on demand. `[]` = snapshot taken, release had
     * none; videos Discogs gains later stay invisible to such records
     * (accepted: same freeze-at-intake semantics as discogsReleaseId).
     * Defaulted so records written before these fields existed still parse on
     * read. Immutable post-intake — omitted from UpdateRecordRequestSchema.
     */
    videos: z.array(DiscogsVideoSchema).max(30).nullable().default(null),
    tracklist: z.array(ReleaseTrackSchema).max(150).nullable().default(null),
    // Discogs Marketplace listing state (server/worker-managed), or null when the
    // record isn't on Discogs. Defaulted to null so records written before this
    // field existed still parse on read (the raw DDB item lacks the key), and so
    // intake — which only sends `listOnDiscogs` intent, never this object — parses.
    // Omitted from both the Create and Update request schemas: a client must never
    // set the listing state directly (the worker/poller own it).
    discogsListing: DiscogsListingSchema.nullable().default(null),
    // Defaulted so records written before this field existed still parse on
    // read (the raw DDB item lacks the key). On CreateRequest the default also
    // fills an all-null object when intake omits it; on UpdateRequest `.partial()`
    // wraps it in optional, which suppresses the default so an unrelated PATCH
    // never silently wipes existing image keys.
    onlineImages: OnlineImagesSchema.default(EMPTY_ONLINE_IMAGES),
    /**
     * When this record was published to the tenant's own webshop, or null when
     * unpublished. Doubles as the publication flag (published = non-null) and
     * as the storefront's `publishedAt` (drives "net binnen deze week").
     * Server-stamped by POST /records/{id}/publish after readiness checks and
     * cleared by /unpublish — omitted from the Create/Update request schemas so
     * a client can never set it directly. NOT the same thing as
     * `channel === 'online'`, which means "offer on the Discogs Marketplace".
     * Union (not `IsoDateSchema.nullable()`) for the same registered-component
     * reason as DiscogsListingSchema's dates. Defaulted so records written
     * before this field existed still parse on read.
     */
    webshopPublishedAt: z.union([IsoDateSchema, z.null()]).default(null),
    createdAt: IsoDateSchema,
    soldAt: IsoDateSchema.nullable(),
    fulfilledAt: IsoDateSchema.nullable(),
    /**
     * Why this copy left the shelf — set when it moves to `missing` or
     * `scrapped`, null while it's live. Server-managed: written by the
     * transition/scrap endpoints, omitted from both request schemas.
     *
     * `.default(null)` (not a bare `.nullable()`) is load-bearing: every record
     * written before this field existed has no such key in DynamoDB, and a
     * `.nullable()` field still requires the key to be PRESENT — so without the
     * default, every pre-existing record would start failing `RecordSchema`
     * parse across get/patch/scrap/transition. Same rationale as `isNew`,
     * `videos`, and `discogsListing` above.
     *
     * Hard-deleted records carry their reason in the audit item's `metadata`
     * instead: by then there is no row left to hold it.
     *
     * Nullable via an explicit union, NOT `RemovalReasonSchema.nullable()` —
     * same trap as `listedAt`/`removedAt` above: `RemovalReason` is a registered
     * component (`.openapi('RemovalReason')`), and `.nullable()` on a registered
     * ref mutates the COMPONENT itself to `type: [string, null]` with `null` in
     * its enum, so every future `$ref: RemovalReason` (a bulk-removal body, a
     * reason filter, an audit query) would inherit a nullability it never asked
     * for. The union references it without mutating it.
     */
    removalReason: z.union([RemovalReasonSchema, z.null()]).default(null),
    removalReasonNote: z.string().max(300).nullable().default(null),
    // ── Reservation + sale linkage (server/worker-managed) ──
    // Set while a copy is held (`status === 'reserved'`). Every record is a
    // unique physical copy, so one copy has AT MOST one active hold — the hold
    // deliberately lives embedded on the record (no separate reservation
    // entity), which is what keeps the anti-double-sell conditional writes
    // simple and race-proof. Two hold kinds share these fields:
    //   - till-basket hold: `reservedSession` set, `reservedUntil` = now+30min
    //     (RESERVATION_TTL_MS in handlers/pos.ts); the 5-min expiry sweeper
    //     auto-releases past it.
    //   - customer hold (drawer "Reserveren"): `reservedSession` null,
    //     `reservationNote` free text, `reservedUntil` = chosen date or
    //     now + the tenant's customerHoldDefaultDays (default 7, max 30) —
    //     NO hold is ever indefinite; the same sweeper cleans everything.
    // All fields are defaulted for read tolerance and omitted from the
    // Create/Update request schemas — a client never sets them directly.
    /** Cognito sub of the staff member holding this reservation, or null when not reserved. */
    reservedBy: z.string().nullable().default(null),
    /** Basket/session id that owns a till hold (null for a customer hold). */
    reservedSession: z.string().nullable().default(null),
    /** When the hold expires; the sweeper releases the copy back to in_stock past this. */
    reservedUntil: z.union([IsoDateSchema, z.null()]).default(null),
    /** When the current hold (basket or customer) was placed, or null. */
    reservedAt: z.union([IsoDateSchema, z.null()]).default(null),
    /**
     * Free-text customer-hold context ("Jan, 06-1234, komt zaterdag"); null
     * for basket holds. Length-capped on the transition REQUEST schema only —
     * the entity stays read-tolerant. PRIVACY NOTE (deliberate trade-off):
     * this note typically carries customer name/phone and is copied into
     * immutable AUDIT# items by every hold-related audit write — that
     * preservation is the point (a released hold stays explainable), but it
     * means note PII outlives the hold. Revisit if an erasure requirement
     * lands.
     */
    reservationNote: z.string().nullable().default(null),
    /**
     * Display snapshot of who placed the hold — the ID-token `email` claim at
     * reserve time (see lib/tenant.ts userEmail), or null. Display only,
     * never authorization. RETENTION NOTE (deliberate trade-off, mirrors
     * reservationNote above): this staff email lives on the LIVE record for
     * the duration of the hold and is returned to every signed-in role; it is
     * a point-in-time snapshot, so it goes stale if the staff member is
     * renamed or leaves, and it persists in audit before-images forever.
     * Cleared with the other reservation fields on every exit from
     * `reserved`. Revisit if a staff-data erasure requirement lands.
     */
    reservedByName: z.string().nullable().default(null),
    /** The sale Transaction this copy sold in (set at sale) — lets returns find sibling lines. */
    saleTransactionId: UlidSchema.nullable().default(null),
  })
  .openapi('Record')

/**
 * Intake request — server fills id, shortId, tenantId, status, locationId, timestamps.
 * Phase 1 keeps supplier/channel optional with defaults to ease parity with the HTML.
 */
export const CreateRecordRequestSchema = RecordSchema.omit({
  id: true,
  tenantId: true,
  shortId: true,
  status: true,
  locationId: true,
  createdAt: true,
  soldAt: true,
  fulfilledAt: true,
  // POS-managed lifecycle — set by the reserve/sale flow, never at intake.
  reservedBy: true,
  reservedSession: true,
  reservedUntil: true,
  reservedAt: true,
  reservationNote: true,
  reservedByName: true,
  saleTransactionId: true,
  // Server/worker-managed: intake signals intent via `listOnDiscogs` (below), not
  // by setting the listing object directly.
  discogsListing: true,
  // Removal bookkeeping — a record cannot be intaked already removed.
  removalReason: true,
  removalReasonNote: true,
  // Webshop publication is its own gated action (POST /records/{id}/publish)
  // with server-side readiness checks — never set at intake.
  webshopPublishedAt: true,
})
  .partial({
    supplierId: true,
    supplierShareSnapshot: true,
    photoKey: true,
    discogsReleaseId: true,
    discogsCoverUrl: true,
    note: true,
    year: true,
    country: true,
    catno: true,
    genre: true,
    subgenre: true,
  })
  .extend({
    requestId: UlidSchema,
    channel: RecordChannelSchema.default('store'),
    /**
     * Intent flag: when the effective channel is `online`, also create a Discogs
     * Marketplace listing for this record (the "Verkoop via Discogs Marketplace"
     * checkbox, default-checked in the UI). Ignored for non-online channels. The
     * handler turns a truthy value into `discogsListing.status = 'pending'` and
     * enqueues the async listing worker; it never blocks the save on Discogs.
     * Defaults to false so an omitting client never lists unintentionally.
     */
    listOnDiscogs: z.boolean().optional().default(false),
  })
  // Reject unknown keys at the external boundary: a frontend sending a field
  // this endpoint doesn't accept (e.g. an attempt to set tenantId or an
  // immutable snapshot field) is a bug, surfaced as a 400 rather than silently
  // dropped. RecordSchema itself stays non-strict — it parses raw DDB items
  // carrying PK/SK/gsi* attributes.
  //
  // isNew deliberately has NO Mint/Mint precondition (an earlier superRefine
  // enforced one; removed at the shop owner's request): "new" is a tax fact,
  // condition a physical one — new stock can arrive transport-damaged (dented
  // sleeve) and must still intake as isNew with an honest grade, exactly as
  // the rest of the system already treats saved records (re-grades and
  // returns keep the flag). The intake form nudges instead: ticking the
  // checkbox pre-fills both grades to Mint, editable afterwards.
  .strict()
  // A cover is only meaningful next to the release it was picked from.
  .refine(b => !b.discogsCoverUrl || !!b.discogsReleaseId, {
    message: 'discogsCoverUrl requires discogsReleaseId',
    path: ['discogsCoverUrl'],
  })
  .openapi('CreateRecordRequest')

// Built from RecordSchema (not CreateRecordRequestSchema) so it does not inherit
// the `.default('store')` that CreateRecordRequestSchema places on `channel`.
// With the default inherited, an empty PATCH body parsed to `{ channel: 'store' }`,
// which both made the handler's ERR_EMPTY_UPDATE branch unreachable and silently
// re-asserted channel='store' on records that were 'online' / 'not_for_sale' / 'scrapped'.
//
// Also omitted: snapshot/provenance fields that must not change post-intake:
//   - supplierShareSnapshot: captured-at-intake percentage; mutating it would
//     retroactively rewrite historical payouts (see RecordSchema field doc).
//   - photoKey / discogsReleaseId: identify the original capture; replacing
//     them post-save would orphan S3 objects and the Discogs trail. If these
//     genuinely need to change, expose a dedicated endpoint with explicit
//     audit semantics rather than allowing it via generic PATCH.
//   - supplierId: the supplier is fixed at intake together with its
//     supplierShareSnapshot. Allowing supplierId to change on PATCH (while the
//     snapshot is immutable) would leave the snapshot pointing at the previous
//     supplier's share percent and corrupt payout reports. Changing supplier
//     must be a deliberate flow that re-snapshots, not a generic PATCH.
//
// isNew is deliberately NOT in this omit list (it was, originally): a missed
// checkbox at intake shouldn't force delete-and-re-scan, so the flag stays
// PATCHable while the copy is unsold. The moment it's in a sold state the
// handler locks it (ERR_RECORD_SOLD, mirroring the channel guard) — the sale
// line snapshotted the VAT treatment actually charged, and the record must
// keep matching it.
export const UpdateRecordRequestSchema = RecordSchema.omit({
  id: true,
  tenantId: true,
  shortId: true,
  status: true,
  locationId: true,
  createdAt: true,
  soldAt: true,
  fulfilledAt: true,
  supplierId: true,
  supplierShareSnapshot: true,
  photoKey: true,
  discogsReleaseId: true,
  // Derived from discogsReleaseId at intake; follows its immutability.
  discogsCoverUrl: true,
  // POS-managed lifecycle — never mutated via a generic PATCH.
  reservedBy: true,
  reservedSession: true,
  reservedUntil: true,
  reservedAt: true,
  reservationNote: true,
  reservedByName: true,
  saleTransactionId: true,
  // Worker/poller-managed lifecycle — never mutated via a generic PATCH (a relist
  // is its own endpoint; a sale/transition updates it as a side effect).
  discogsListing: true,
  // Listen-snapshot, captured at intake alongside discogsReleaseId and immutable
  // for the same reason. ALSO omitted to dodge the `.partial()` default trap
  // documented below: with `.default(null)` inherited from RecordSchema, an
  // empty PATCH would parse to `{ videos: null, tracklist: null }` and silently
  // wipe the snapshot on every unrelated PATCH.
  videos: true,
  tracklist: true,
  // Removal bookkeeping — owned by the transition/scrap endpoints, which pair it
  // with the status change. ALSO omitted to dodge the `.partial()` default trap
  // documented below: with `.default(null)` inherited from RecordSchema, an empty
  // PATCH would parse to `{ removalReason: null }` and silently wipe the reason
  // off an already-scrapped record on every unrelated PATCH.
  removalReason: true,
  removalReasonNote: true,
  // Webshop publication state is owned by the publish/unpublish action pair
  // (server-stamped after readiness checks) — never mutated via a generic
  // PATCH. ALSO omitted to dodge the `.partial()` default trap documented
  // below: with `.default(null)` inherited from RecordSchema, an empty PATCH
  // would parse to `{ webshopPublishedAt: null }` and silently UNPUBLISH the
  // record on every unrelated PATCH.
  webshopPublishedAt: true,
})
  .partial()
  // Override onlineImages to a plain optional: RecordSchema gives it a
  // `.default(all-null)` for read tolerance, but `.partial()` does NOT suppress
  // that default, so an empty PATCH body would otherwise parse to
  // `{ onlineImages: {…all null} }` — defeating the handler's ERR_EMPTY_UPDATE
  // guard AND silently wiping a record's existing image keys on every unrelated
  // PATCH. As a plain optional, an omitted onlineImages stays omitted.
  // Same rationale for `description`: RecordSchema gives it `.default(null)` for
  // read tolerance, but `.partial()` does NOT suppress that default, so an empty
  // PATCH body would otherwise parse to `{ description: null }` — defeating the
  // ERR_EMPTY_UPDATE guard and wiping an existing description on every unrelated
  // PATCH. As a plain optional, an omitted description stays omitted.
  .extend({
    onlineImages: OnlineImagesSchema.optional(),
    description: z.string().max(2000).nullable().optional(),
    // Same rationale as description above: RecordSchema gives `label` a
    // `.default(null)` for read tolerance, but `.partial()` does NOT suppress
    // that default, so an empty PATCH body would otherwise parse to
    // `{ label: null }` — defeating the ERR_EMPTY_UPDATE guard and wiping an
    // existing label on every unrelated PATCH. As a plain optional, an omitted
    // label stays omitted.
    label: z.string().max(120).nullable().optional(),
    // Same rationale as description/onlineImages above: RecordSchema gives
    // sleeveCondition a `.default('VG+')` for read tolerance, but `.partial()`
    // does NOT suppress that default, so an empty PATCH body would otherwise
    // parse to `{ sleeveCondition: 'VG+' }` — defeating the ERR_EMPTY_UPDATE
    // guard and silently re-asserting the default on every unrelated PATCH.
    // As a plain optional, an omitted sleeveCondition stays omitted.
    sleeveCondition: SleeveConditionSchema.optional(),
    // Same rationale again: RecordSchema defaults isNew to false for read
    // tolerance, but `.partial()` does NOT suppress that default — an empty
    // PATCH body would otherwise parse to `{ isNew: false }`, defeating the
    // ERR_EMPTY_UPDATE guard and silently CLEARING the flag on every
    // unrelated PATCH of an unsold record. As a plain optional, an omitted
    // isNew stays omitted (the handler's sold-lock guard handles the rest).
    isNew: z.boolean().optional(),
    // Same rationale as label above for the two webshop display fields: both
    // carry `.default(null)` on RecordSchema for read tolerance, which
    // `.partial()` does not suppress — as plain optionals an unrelated PATCH
    // can never wipe an existing subtitle or format.
    subtitle: z.string().max(200).nullable().optional(),
    format: z.string().max(80).nullable().optional(),
  })
  // Same rationale as CreateRecordRequestSchema: unknown keys on a PATCH (e.g.
  // an attempt to mutate an omitted immutable field) are a 400, not a silent drop.
  .strict()
  .openapi('UpdateRecordRequest')

/**
 * Statuses a record can be moved to via the generic transition endpoint
 * (POST /records/{id}/transition). Deliberately EXCLUDES:
 *   - `scrapped`: has its own endpoint (/scrap) with distinct audit semantics.
 *   - `not_for_sale`: set by changing the channel (it doubles as a channel).
 * The backend further restricts which target is legal from a given current
 * status via an allowed-transitions matrix in the handler.
 */
export const TransitionStatusSchema = z
  .enum(['in_stock', 'reserved', 'sold', 'sold_online', 'fulfilled', 'returned', 'missing'])
  .openapi('TransitionStatus')

export const TransitionRecordRequestSchema = z
  .object({
    status: TransitionStatusSchema,
    /**
     * Customer-hold context ("Jan, 06-1234, komt zaterdag"). Only valid when
     * `status === 'reserved'` — the superRefine below rejects it elsewhere,
     * keeping the strict-boundary philosophy (a misplaced field is a 400,
     * never a silent drop).
     */
    note: z.string().trim().min(1).max(300).optional(),
    /**
     * Auto-release moment for a customer hold; the expiry sweeper releases
     * the copy past this. Omitted → the handler applies the tenant's
     * customerHoldDefaultDays (see schemas/settings.ts) — NO hold is ever
     * indefinite. The handler additionally rejects past dates and dates
     * beyond 30 days out.
     */
    heldUntil: IsoDateSchema.optional(),
    /**
     * Why the copy is being marked missing (Vermist). Only valid when
     * `status === 'missing'` — rejected elsewhere by the superRefine below,
     * same strict-boundary rule as note/heldUntil. Optional so an older client
     * that posts no reason still transitions successfully.
     */
    removalReason: MissingReasonSchema.optional(),
    removalReasonNote: RemovalReasonNoteSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.status !== 'reserved' && (v.note !== undefined || v.heldUntil !== undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'note/heldUntil are only allowed when status is reserved',
        path: [v.note !== undefined ? 'note' : 'heldUntil'],
      })
    }
    if (
      v.status !== 'missing' &&
      (v.removalReason !== undefined || v.removalReasonNote !== undefined)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'removalReason/removalReasonNote are only allowed when status is missing',
        path: [v.removalReason !== undefined ? 'removalReason' : 'removalReasonNote'],
      })
    }
    removalReasonRefinement(v, ctx)
  })
  .openapi('TransitionRecordRequest')

/**
 * Scrap (Afdanken) request body. `POST /records/{id}/scrap` took NO body before
 * removal reasons existed, so every field is optional and `parseJsonBody`
 * defaults an absent body to `{}` — an older client that posts nothing still
 * scraps successfully, it just records no reason.
 */
export const ScrapRecordRequestSchema = z
  .object({
    removalReason: ScrapReasonSchema.optional(),
    removalReasonNote: RemovalReasonNoteSchema.optional(),
  })
  .strict()
  .superRefine(removalReasonRefinement)
  .openapi('ScrapRecordRequest')

/**
 * Hard-delete request body. Unlike scrap, the reason is REQUIRED: the delete
 * leaves no row behind, so the audit item's metadata is the only surviving
 * record that it happened and why.
 */
export const DeleteRecordRequestSchema = z
  .object({
    removalReason: DeleteReasonSchema,
    removalReasonNote: RemovalReasonNoteSchema.optional(),
  })
  .strict()
  .superRefine(removalReasonRefinement)
  .openapi('DeleteRecordRequest')

/**
 * Hard-delete response. There is no record left to return, so this echoes the
 * id that was removed plus the short id that just returned to the pool (it may
 * be re-issued to a different copy later).
 */
export const DeleteRecordResponseSchema = z
  .object({
    id: UlidSchema,
    deletedShortId: ShortIdSchema,
  })
  .openapi('DeleteRecordResponse')

/**
 * Legal status transitions for POST /records/{id}/transition, keyed by the
 * record's CURRENT status. The transition handler enforces this matrix, and the
 * frontend drawer derives its action buttons from this same export — one source
 * of truth, so the two can't drift (a button the API would 409, or a hidden
 * legal move). `scrapped` (own /scrap endpoint) and `not_for_sale` (set via a
 * channel change) are dead-end sources with no targets here.
 *
 * Typed as a mapped type rather than `Record<…>` because `Record` is shadowed by
 * the entity type exported from this module.
 */
export const ALLOWED_TRANSITIONS: { [K in RecordStatus]: readonly TransitionStatus[] } = {
  in_stock: ['reserved', 'sold', 'sold_online', 'missing'],
  reserved: ['in_stock', 'sold', 'sold_online', 'missing'],
  sold: ['returned', 'in_stock'],
  sold_online: ['fulfilled', 'returned', 'missing'],
  fulfilled: ['returned'],
  returned: ['in_stock', 'missing'],
  missing: ['in_stock'],
  scrapped: [],
  not_for_sale: [],
}

export const ListRecordsQuerySchema = z
  .object({
    // `shelf` is the cheap entry point: it Queries GSI2's bounded active/closed
    // partition. Omit it (and `status`) to fall back to a Query over the tenant's
    // entire RECORD# history partition.
    shelf: ShelfStateSchema.optional(),
    channel: RecordChannelSchema.optional(),
    status: RecordStatusSchema.optional(),
    supplierId: UlidSchema.optional(),
    boxId: BoxIdSchema.optional(),
    /**
     * Filter to records with this Discogs Marketplace listing status (e.g. `listed`
     * for "everything currently live on Discogs"). Applied as an in-Lambda predicate;
     * a record with no `discogsListing` at all never matches, regardless of value.
     */
    discogsListingStatus: DiscogsListingStatusSchema.optional(),
    /**
     * Comma-separated list of disc grades to include (e.g. "VG+,NM"). Multi-select:
     * a record matches if its `condition` is any of the listed grades. Applied as an
     * in-Lambda predicate over the queried partition. Resolves to `[]` when absent.
     */
    conditions: z
      .string()
      .optional()
      .transform(s => (s && s.length > 0 ? s.split(',') : []))
      .pipe(z.array(RecordConditionSchema)),
    /**
     * Inclusive price range bounds. Lenient numeric (not strict `MoneySchema`) so a
     * user typing "10" in a filter box works; compared numerically in-Lambda against
     * each record's decimal-string price. A blank value is dropped (see
     * `blankToUndefined`) rather than coerced to 0.
     */
    priceMin: z.preprocess(blankToUndefined, z.coerce.number().nonnegative().optional()),
    priceMax: z.preprocess(blankToUndefined, z.coerce.number().nonnegative().optional()),
    /** Inclusive release-year range. In-Lambda predicate; records with null year are excluded. */
    yearMin: z.preprocess(blankToUndefined, z.coerce.number().int().min(1900).max(2100).optional()),
    yearMax: z.preprocess(blankToUndefined, z.coerce.number().int().min(1900).max(2100).optional()),
    /**
     * Free-text search across artist + title (accent/case-insensitive), with a
     * numeric term also matching a `shortId` prefix. Applied as one more in-Lambda
     * predicate over the queried partition — it composes with every other filter
     * above and honours `shelf`/`channel`/`status`/`box`. The frontend routes an
     * exact 6-digit id to GET /records/by-short instead.
     */
    q: z.string().trim().min(1).max(80).optional(),
    /**
     * Sort field and direction, applied in-Lambda over the complete matched set.
     * - `createdAt`: also uses the index's natural ordering (ScanIndexForward).
     * - `price` / `condition` / `year`: sorted in-Lambda after the partition read.
     * Default: `createdAt:desc` (newest first).
     */
    sort: z
      .string()
      .regex(/^(createdAt|price|condition|year):(asc|desc)$/)
      .default('createdAt:desc'),
  })
  .openapi('ListRecordsQuery')

export const ListRecordsResponseSchema = z
  .object({
    items: z.array(RecordSchema),
    /**
     * True when the result was capped — either more than RESULT_CAP records
     * matched, or the partition read bound was hit before the partition was fully
     * read. The UI surfaces a "showing first N, refine your search" notice.
     */
    truncated: z.boolean().optional(),
  })
  .openapi('ListRecordsResponse')

export type Record = z.infer<typeof RecordSchema>
export type OnlineImages = z.infer<typeof OnlineImagesSchema>
export type DiscogsListing = z.infer<typeof DiscogsListingSchema>
export type DiscogsListingStatus = z.infer<typeof DiscogsListingStatusSchema>
export type RecordChannel = z.infer<typeof RecordChannelSchema>
export type RecordCondition = z.infer<typeof RecordConditionSchema>
export type SleeveCondition = z.infer<typeof SleeveConditionSchema>
export type RecordStatus = z.infer<typeof RecordStatusSchema>
export type CreateRecordRequest = z.infer<typeof CreateRecordRequestSchema>
export type UpdateRecordRequest = z.infer<typeof UpdateRecordRequestSchema>
export type TransitionStatus = z.infer<typeof TransitionStatusSchema>
export type TransitionRecordRequest = z.infer<typeof TransitionRecordRequestSchema>
export type RemovalReason = z.infer<typeof RemovalReasonSchema>
export type MissingReason = z.infer<typeof MissingReasonSchema>
export type ScrapReason = z.infer<typeof ScrapReasonSchema>
export type DeleteReason = z.infer<typeof DeleteReasonSchema>
export type ScrapRecordRequest = z.infer<typeof ScrapRecordRequestSchema>
export type DeleteRecordRequest = z.infer<typeof DeleteRecordRequestSchema>
export type DeleteRecordResponse = z.infer<typeof DeleteRecordResponseSchema>
